import { useState } from "react";
import { Plus, MoreHorizontal, Trash2, Pencil, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUsers, useUpdateUser, useDeleteUser } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { formatRelativeTime } from "@/lib/formatters";
import { AddUserDialog } from "./add-user-dialog";

const roleColors: Record<string, string> = {
  owner: "bg-purple-500/20 text-purple-400",
  admin: "bg-blue-500/20 text-blue-400",
  analyst: "bg-green-500/20 text-green-400",
  viewer: "bg-gray-500/20 text-gray-400",
};

export function UsersTab() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<string>("");
  const [roleError, setRoleError] = useState<string | null>(null);

  const { user: currentUser } = useAuth();
  const { data, isLoading, isError } = useUsers();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  const users = data?.users ?? [];

  const currentUserRecord = users.find((u) => u.email === currentUser?.email);
  const currentUserRole = currentUserRecord?.role ?? "viewer";
  const allowedRoles =
    currentUserRole === "owner"
      ? (["owner", "admin", "analyst", "viewer"] as const)
      : (["admin", "analyst", "viewer"] as const);

  function handleEditRole(userId: string, currentRole: string) {
    setEditingUserId(userId);
    setEditRole(currentRole);
    setRoleError(null);
  }

  function handleSaveRole(userId: string, previousRole: string) {
    updateMutation.mutate(
      { userId, role: editRole },
      {
        onSuccess: () => {
          setEditingUserId(null);
          setRoleError(null);
        },
        onError: (err) => {
          setEditRole(previousRole);
          setRoleError(
            err instanceof Error ? err.message : "Failed to update role",
          );
        },
      },
    );
  }

  function handleDelete(userId: string) {
    if (window.confirm("Are you sure you want to remove this user?")) {
      deleteMutation.mutate(userId);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          Team Members
        </h3>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add
        </Button>
      </div>

      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load users.
        </div>
      )}

      {roleError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {roleError}
        </div>
      )}

      {users.length === 0 && !isError ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <div className="rounded-full bg-muted p-3">
            <Users className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-sm font-semibold">No team members</h3>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden sm:table-cell">Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="hidden md:table-cell">Last Login</TableHead>
              <TableHead className="w-[40px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell className="hidden text-muted-foreground sm:table-cell">
                  {user.email}
                </TableCell>
                <TableCell>
                  {editingUserId === user.id ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                      >
                        {allowedRoles.map((r) => (
                          <option key={r} value={r}>
                            {r.charAt(0).toUpperCase() + r.slice(1)}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleSaveRole(user.id, user.role)}
                        disabled={updateMutation.isPending}
                      >
                        Save
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setEditingUserId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        roleColors[user.role] ?? "bg-muted text-muted-foreground"
                      }`}
                    >
                      {user.role}
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                  {user.last_login
                    ? formatRelativeTime(user.last_login)
                    : "Never"}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="More options" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={user.email === currentUser?.email}
                        onClick={
                          user.email === currentUser?.email
                            ? undefined
                            : () => handleEditRole(user.id, user.role)
                        }
                        className={
                          user.email === currentUser?.email
                            ? "pointer-events-none opacity-50"
                            : ""
                        }
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit Role
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={user.email === currentUser?.email}
                        className={
                          user.email === currentUser?.email
                            ? "pointer-events-none opacity-50"
                            : "text-destructive"
                        }
                        onClick={
                          user.email === currentUser?.email
                            ? undefined
                            : () => handleDelete(user.id)
                        }
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddUserDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
